import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  QrCode,
  Search,
  Check,
  AlertCircle,
  RefreshCw,
  Camera,
  Keyboard,
  Sparkles,
  Upload,
  Flashlight,
  StopCircle,
} from 'lucide-react';
import { fetchProductByBarcode } from '../lib/ingredientUtils';
import { compressImage } from '../lib/imageUtils';
import { localStore } from '../lib/storage';
import { analyzeImageDirectOrProxy } from '../lib/gemini';
import { useBarcodeScanner } from '../lib/useBarcodeScanner';

interface BarcodeScannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectProduct: (product: { name: string; ingredients: string[] }) => void;
}

export const BarcodeScannerModal: React.FC<BarcodeScannerModalProps> = ({
  isOpen,
  onClose,
  onSelectProduct,
}) => {
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorNotice, setErrorNotice] = useState<string | null>(null);
  const [hasDetected, setHasDetected] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const [scannedResult, setScannedResult] = useState<{
    name: string;
    brand?: string;
    ingredients: string[];
  } | null>(null);

  const barcodePhotoFileRef = useRef<HTMLInputElement>(null);

  const handleLookup = async (codeToLookup: string) => {
    const cleanCode = codeToLookup.trim();
    if (!cleanCode || cleanCode.length < 6) {
      setErrorNotice('Please enter a valid barcode number (e.g. 5000111043452).');
      return;
    }

    setIsLoading(true);
    setErrorNotice(null);

    const product = await fetchProductByBarcode(cleanCode);
    setIsLoading(false);

    if (product) {
      setScannedResult(product);
      stopScannerCamera();
    } else {
      setErrorNotice(`Product not found for barcode "${cleanCode}". You can enter the meal name in Describe.`);
      if (!manualMode) {
        setTimeout(() => {
          setHasDetected(false);
        }, 2500);
      }
    }
  };

  const {
    videoRef,
    isCameraActive,
    cameraError,
    torchAvailable,
    isTorchOn,
    startCamera: startScannerCamera,
    stopCamera: stopScannerCamera,
    toggleTorch,
    decodeFromPhoto,
  } = useBarcodeScanner({
    onDetected: (code) => {
      setHasDetected(true);
      setBarcodeInput(code);
      handleLookup(code);
    },
    enabled: isOpen && !manualMode && !scannedResult,
  });

  const handlePhotoUpload = async (file: File) => {
    if (!file) return;
    setIsLoading(true);
    setErrorNotice(null);
    try {
      const detectedBarcode = await decodeFromPhoto(file);

      if (detectedBarcode) {
        setBarcodeInput(detectedBarcode);
        await handleLookup(detectedBarcode);
        return;
      }

      // If no barcode detected, extract product name and ingredients using Vision AI
      const compressed = await compressImage(file, 1024, 0.82);
      const data = await analyzeImageDirectOrProxy(compressed);

      if (data.success && (data.suggested_name || (data.ingredients_tags && data.ingredients_tags.length > 0))) {
        const name = data.suggested_name || data.dish_name || 'Scanned Product';
        const ingredients = Array.isArray(data.ingredients_tags) ? data.ingredients_tags : [];
        setScannedResult({
          name,
          ingredients,
        });
        stopScannerCamera();
        return;
      }

      setErrorNotice('Could not detect barcode or ingredients in photo. Please try a clearer picture or enter digits manually.');
    } catch (err) {
      console.warn('Image barcode decode failed:', err);
      setErrorNotice('Could not detect barcode in photo. Please try a clearer picture or enter digits manually.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !manualMode && !scannedResult) {
      startScannerCamera();
    } else {
      stopScannerCamera();
    }

    return () => {
      stopScannerCamera();
    };
  }, [isOpen, manualMode, scannedResult, startScannerCamera, stopScannerCamera]);

  const handleResetScan = () => {
    setScannedResult(null);
    setBarcodeInput('');
    setErrorNotice(null);
    setManualMode(false);
    setHasDetected(false);
  };

  const handleConfirm = () => {
    if (scannedResult) {
      onSelectProduct({
        name: scannedResult.name,
        ingredients: scannedResult.ingredients,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-md flex flex-col justify-end animate-fade-in font-[-apple-system,BlinkMacSystemFont,'SF_Pro_Text',sans-serif]">
      <div className="bg-[#F8F9FA] dark:bg-[#12141A] text-slate-900 dark:text-white rounded-t-[36px] max-h-[92vh] flex flex-col w-full max-w-md mx-auto border-t border-slate-200/80 dark:border-zinc-800/80 shadow-2xl overflow-hidden">
        
        {/* iOS Drag Handle */}
        <div className="w-full pt-3 pb-1 flex justify-center">
          <div className="w-10 h-1.5 rounded-full bg-slate-300 dark:bg-zinc-700" />
        </div>

        {/* Header */}
        <div className="px-6 py-2.5 flex items-center justify-between border-b border-slate-200/60 dark:border-zinc-800">
          <div className="flex items-center space-x-2">
            <QrCode className="w-5 h-5 text-slate-700 dark:text-zinc-300" />
            <h2 className="text-xl font-black uppercase tracking-tight">Barcode Scanner</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-200/70 dark:bg-zinc-800 flex items-center justify-center text-slate-700 dark:text-zinc-300 hover:text-rose-500 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 text-left">
          
          {/* Subtitle */}
          <p className="text-xs text-slate-500 dark:text-zinc-400">
            Powered by Open Food Facts. Auto-detects barcodes in real time and extracts verified ingredient tags.
          </p>

          {/* ========================================================================= */}
          {/* VIEW 1: SCANNED PRODUCT RESULT                                           */}
          {/* ========================================================================= */}
          {scannedResult ? (
            <div className="p-4 bg-white dark:bg-[#1A1D24] rounded-3xl border border-slate-200 dark:border-zinc-800 space-y-3 animate-fade-in shadow-sm">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-black tracking-widest text-slate-400 uppercase">
                  FOUND PRODUCT
                </span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 font-bold">
                  Verified In Database
                </span>
              </div>

              <div>
                <h3 className="text-base font-black text-slate-900 dark:text-white leading-tight">
                  {scannedResult.name}
                </h3>
                {scannedResult.brand && (
                  <p className="text-xs text-slate-400 mt-0.5">Brand: {scannedResult.brand}</p>
                )}
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400">
                  <span>Canonical Ingredients ({scannedResult.ingredients.length}):</span>
                  <span className="text-[10px] text-emerald-600 font-bold">"May contain" excluded</span>
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-36 overflow-y-auto">
                  {scannedResult.ingredients.map((ing) => (
                    <span
                      key={ing}
                      className="px-2.5 py-1 bg-slate-100 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 font-semibold text-xs rounded-xl border border-slate-200/60 dark:border-zinc-700/60"
                    >
                      {ing}
                    </span>
                  ))}
                  {scannedResult.ingredients.length === 0 && (
                    <span className="text-xs text-slate-400">No ingredients specified in Open Food Facts database</span>
                  )}
                </div>
              </div>

              <div className="space-y-2 pt-1">
                <button
                  type="button"
                  onClick={handleConfirm}
                  className="w-full py-3.5 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black font-black text-xs uppercase tracking-wider active:scale-95 transition-all flex items-center justify-center space-x-1.5 shadow-md"
                >
                  <Check className="w-4 h-4 stroke-[3]" />
                  <span>Use This Food In Meal Log</span>
                </button>

                <button
                  type="button"
                  onClick={handleResetScan}
                  className="w-full py-2.5 rounded-2xl bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 text-slate-700 dark:text-zinc-300 font-bold text-xs transition-all text-center"
                >
                  Scan Another Barcode
                </button>
              </div>
            </div>
          ) : !manualMode ? (
            /* ========================================================================= */
            /* VIEW 2: LIVE CAMERA VIEWFINDER WITH RETICLE BOX OVERLAY                   */
            /* ========================================================================= */
            <div className="space-y-3 animate-fade-in">
              {/* Camera Frame Container */}
              <div className="relative w-full aspect-[4/3] max-h-[310px] bg-black rounded-3xl overflow-hidden flex items-center justify-center shadow-inner">
                {/* Live Video Feed */}
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  autoPlay
                  className="w-full h-full object-cover"
                />

                {/* Top controls: Torch & Close */}
                {isCameraActive && torchAvailable && (
                  <div className="absolute top-3 right-3 flex items-center space-x-1.5 z-10">
                    <button
                      type="button"
                      onClick={toggleTorch}
                      className={`p-2.5 rounded-xl backdrop-blur-md border transition-all ${
                        isTorchOn
                          ? 'bg-amber-500 text-white border-amber-400 shadow-md shadow-amber-500/30'
                          : 'bg-black/50 text-white/80 border-white/20 hover:bg-black/70'
                      }`}
                      title={isTorchOn ? 'Turn Torch Off' : 'Turn Torch On'}
                    >
                      <Flashlight className="w-4 h-4" />
                    </button>
                  </div>
                )}

                {/* Box Overlay / Reticle Mask */}
                {isCameraActive && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    {/* Targeting Box Frame */}
                    <div
                      className={`relative w-64 h-36 rounded-2xl transition-all duration-300 ${
                        hasDetected
                          ? 'border-2 border-emerald-400 scale-105 shadow-[0_0_24px_rgba(52,211,153,0.8)]'
                          : 'border border-white/30 shadow-[0_0_0_9999px_rgba(0,0,0,0.55)]'
                      }`}
                    >
                      {/* 4 Corner Framing Accents */}
                      <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-emerald-400 rounded-tl-xl" />
                      <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-emerald-400 rounded-tr-xl" />
                      <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-emerald-400 rounded-bl-xl" />
                      <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-emerald-400 rounded-br-xl" />

                      {/* Animated Laser Scanning Line */}
                      {!hasDetected && (
                        <div className="absolute inset-x-0 h-0.5 bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)] animate-scanline" />
                      )}

                      {/* Success Pulse Indicator */}
                      {hasDetected && (
                        <div className="absolute inset-0 flex items-center justify-center bg-emerald-500/20 backdrop-blur-xs">
                          <Check className="w-8 h-8 text-emerald-400 animate-bounce stroke-[3]" />
                        </div>
                      )}
                    </div>

                    {/* Guide Text Below Frame */}
                    <span className="mt-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md text-[10px] font-bold text-white/90 uppercase tracking-wider">
                      {hasDetected ? 'Barcode Found! Looking up...' : '360° Omnidirectional Scan'}
                    </span>
                  </div>
                )}

                {/* Loading or Connecting Overlay */}
                {!isCameraActive && !cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 space-y-2 text-white">
                    <RefreshCw className="w-6 h-6 animate-spin text-emerald-400" />
                    <span className="text-xs font-bold">Starting camera viewfinder...</span>
                  </div>
                )}

                {/* Camera Permission or Connection Error */}
                {cameraError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-zinc-900 text-center space-y-2.5 text-white">
                    <AlertCircle className="w-7 h-7 text-amber-400" />
                    <p className="text-xs text-zinc-300 font-medium">{cameraError}</p>
                    <button
                      type="button"
                      onClick={startScannerCamera}
                      className="px-4 py-2 rounded-xl bg-white text-black text-xs font-black"
                    >
                      Retry Camera
                    </button>
                  </div>
                )}
              </div>

              {/* Hidden Photo Input */}
              <input
                ref={barcodePhotoFileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onClick={(e) => {
                  (e.target as HTMLInputElement).value = '';
                }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handlePhotoUpload(file);
                }}
              />

              {/* Status and Action Buttons */}
              <div className="flex items-center justify-between pt-1 gap-2">
                <span className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 truncate">
                  {isLoading ? 'Fetching product...' : 'Ready to scan'}
                </span>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => barcodePhotoFileRef.current?.click()}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-slate-200/80 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 text-xs font-bold hover:bg-slate-300 transition-all active:scale-95"
                  >
                    <Upload className="w-3.5 h-3.5" />
                    <span>Upload</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setManualMode(true)}
                    className="inline-flex items-center space-x-1.5 px-3 py-1.5 rounded-xl bg-slate-200/80 dark:bg-zinc-800 text-slate-800 dark:text-zinc-200 text-xs font-bold hover:bg-slate-300 transition-all active:scale-95"
                  >
                    <Keyboard className="w-3.5 h-3.5" />
                    <span>Manual</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* ========================================================================= */
            /* VIEW 3: MANUAL BARCODE DIGITS INPUT                                       */
            /* ========================================================================= */
            <div className="space-y-3 animate-fade-in">
              <div className="flex items-center justify-between">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 dark:text-zinc-400">
                  Enter Barcode Digits
                </label>
                <button
                  type="button"
                  onClick={() => setManualMode(false)}
                  className="inline-flex items-center space-x-1 text-xs font-bold text-emerald-600 dark:text-emerald-400"
                >
                  <Camera className="w-3.5 h-3.5" />
                  <span>Back to Camera Scanner</span>
                </button>
              </div>

              <div className="flex space-x-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={barcodeInput}
                  onChange={(e) => setBarcodeInput(e.target.value)}
                  placeholder="e.g. 5000111043452"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleLookup(barcodeInput);
                    }
                  }}
                  className="flex-1 px-4 py-3 bg-white dark:bg-[#1A1D24] border border-slate-200 dark:border-zinc-800 rounded-2xl text-sm font-bold text-slate-900 dark:text-white focus:outline-none"
                />
                <button
                  type="button"
                  disabled={isLoading || !barcodeInput.trim()}
                  onClick={() => handleLookup(barcodeInput)}
                  className="px-5 py-3 rounded-2xl bg-slate-950 dark:bg-white text-white dark:text-black font-black text-xs uppercase tracking-wider active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center space-x-1"
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      <span>Lookup</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {errorNotice && (
            <div className="p-3 bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-2xl text-xs text-rose-700 dark:text-rose-300 flex items-start space-x-2 animate-fade-in">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{errorNotice}</span>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
